import { ChangeEvent, useState } from "react";
import { useAppContext } from "../../../context/AuthContext";
import { Feedback } from "../../../controller/Feedback";
import QuestionnaireType, { QuestionnaireAnswerSubmission } from "../../../interfaces/feedback/QuestionnaireType";
import MessageType from "../../../utils/MessageType";
import UserRole from "../../../utils/UserRole";
import { STUDENT_QUESTIONNAIRE, TEACHER_QUESTIONNAIRE } from "./questionnaire";

interface FeedbackPageServiceExport {
  handleMessageChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  createFeedback: () => void;
  message: string;
  loading: boolean;
  questionnaire: QuestionnaireType;
  getAnswer: (questionId: string) => string;
  selectAnswer: (questionId: string, answerId: string) => void;
  clearAnswer: (questionId: string) => void;
  canSend: boolean;
}

export function FeedbackPageService(): FeedbackPageServiceExport {
  const { showMessage, selectedCourse } = useAppContext();
  const [message, setMessage] = useState<string>("");
  // questionId -> selected answerId (single answer per question)
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const { isTeacher } = UserRole();
  const questionnaire = isTeacher ? TEACHER_QUESTIONNAIRE : STUDENT_QUESTIONNAIRE;

  const answers: QuestionnaireAnswerSubmission[] = Object.entries(selected)
    .filter(([, answerId]) => !!answerId)
    .map(([questionId, answerId]) => ({ questionId, answerId }));
  const canSend = !!message || answers.length > 0;

  const createFeedback = () => {
    setLoading(true);
    const body = { courseId: selectedCourse?.id, teacher: isTeacher, message: message, answers: answers };
    Feedback.createFeedback(body)
      .then(() => {
        showMessage("Thank you for your feedback.", MessageType.SUCCESS);
        setMessage("");
        setSelected({});
      })
      .catch((err: any) => showMessage(err.response?.data, MessageType.ERROR))
      .finally(() => setLoading(false));
  };

  const handleMessageChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setMessage(e.target.value);

  const getAnswer = (questionId: string) => selected[questionId] ?? "";

  const selectAnswer = (questionId: string, answerId: string) => setSelected((prev) => ({ ...prev, [questionId]: answerId }));

  const clearAnswer = (questionId: string) => setSelected((prev) => ({ ...prev, [questionId]: "" }));

  return { handleMessageChange, createFeedback, message, loading, questionnaire, getAnswer, selectAnswer, clearAnswer, canSend };
}
export type { FeedbackPageServiceExport };
